import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger } from '@mono/ui-components';
import { Check, Pencil } from 'lucide-react';
import { useState } from 'react';
/**
 * EditableField — click-to-edit inline field with a searchable dropdown.
 *
 * Renders as a static chip by default. Click to open a combobox popover
 * with search/filter. Selecting an option fires `onSave` and closes.
 *
 * Accessible: keyboard navigable, ARIA labels, focus management.
 * Used for solicitation metadata (NAICS, contract type, vehicle, etc.).
 */ export function EditableField({ icon, label, value, displayValue, options, onSave, placeholder = 'Search...', emptyMessage = 'No results found.' }) {
    const [open, setOpen] = useState(false);
    const display = displayValue || value || '—';
    return /*#__PURE__*/ _jsxs(Popover, {
        open: open,
        onOpenChange: setOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    className: "group inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs transition-colors hover:border-blue-300 hover:bg-blue-50",
                    "aria-label": `Edit ${label}`,
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-slate-500",
                            children: icon
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "text-slate-500",
                            children: [
                                label,
                                ":"
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "truncate font-medium text-slate-800",
                            children: display
                        }),
                        /*#__PURE__*/ _jsx(Pencil, {
                            className: "h-3 w-3 flex-shrink-0 text-slate-300 transition-colors group-hover:text-blue-500"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PopoverContent, {
                className: "w-64 p-0",
                align: "start",
                children: /*#__PURE__*/ _jsxs(Command, {
                    label: `Search ${label}`,
                    children: [
                        /*#__PURE__*/ _jsx(CommandInput, {
                            placeholder: placeholder,
                            className: "text-sm"
                        }),
                        /*#__PURE__*/ _jsxs(CommandList, {
                            children: [
                                /*#__PURE__*/ _jsx(CommandEmpty, {
                                    className: "py-3 text-center text-xs text-slate-500",
                                    children: emptyMessage
                                }),
                                /*#__PURE__*/ _jsx(CommandGroup, {
                                    children: options.map((option)=>/*#__PURE__*/ _jsxs(CommandItem, {
                                            value: option.label,
                                            onSelect: ()=>{
                                                onSave(option.value);
                                                setOpen(false);
                                            },
                                            className: "flex items-center gap-2 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsx(Check, {
                                                    className: `h-3.5 w-3.5 flex-shrink-0 ${value === option.value ? 'text-blue-600' : 'text-transparent'}`
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "min-w-0 flex-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "truncate text-slate-800",
                                                            children: option.label
                                                        }),
                                                        option.description && /*#__PURE__*/ _jsx("div", {
                                                            className: "truncate text-[11px] text-slate-500",
                                                            children: option.description
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, option.value))
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
